import debounce from 'lodash/debounce';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';
import type { UsePaginationProps } from 'react-instantsearch-core';
import { usePagination } from 'react-instantsearch-core';
import { useIntl } from 'react-intl';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { useRouter } from '@/hooks/useRouter';
import { messages } from '@/messages/boutique';

import { getPaginationUrl } from './utils';

import * as S from './HitsPagination.styles';

export const HitsPagination = (props: UsePaginationProps) => {
  const { currentRefinement, isFirstPage, isLastPage, nbPages, pages, refine } =
    usePagination(props);
  const { formatMessage } = useIntl();
  const { locale, push, query } = useRouter();
  const pathname = usePathname();

  const queryNbPages = parseInt(query.page as string);

  const handleClick = useCallback(
    (page: number) => {
      scrollTo({ top: 0 });
      window.parentIFrame?.scrollToOffset(0, 0);
      refine(page);
    },
    [refine, push, pathname, locale],
  );

  // Needed because it's not possible to mock the debounce function in tests
  const debouncedHandleClick =
    process.env.NODE_ENV === 'test' ? handleClick : debounce(handleClick, 300);

  if (nbPages <= 1 || nbPages < queryNbPages || queryNbPages <= 0) {
    return null;
  }

  return (
    <>
      <S.Wrapper
        data-test-id='HitsPaginationWrapper'
        gridGap={2}
        justifyItems='center'
        padding={2}
      >
        <Flex alignItems='center' gridGap={2}>
          {!isFirstPage && (
            <S.ChevronLink
              $isDisabled={isFirstPage}
              aria-disabled={isFirstPage}
              aria-label={
                getLinkAriaLabel(
                  getPaginationUrl(
                    pathname as string,
                    currentRefinement,
                    locale,
                  ),
                ) || 'Go to previous page'
              }
              data-test-id='HitsPaginationChevronLink-FirstPage'
              href={getPaginationUrl(
                pathname as string,
                currentRefinement,
                locale,
              )}
              onClick={(evt) => {
                if (isFirstPage) {
                  return;
                }
                evt.preventDefault();
                debouncedHandleClick(currentRefinement - 1);
              }}
            >
              <Icon
                color={isFirstPage ? 'disableGrey' : 'secondary'}
                name='chevronLeft'
                variant='bold'
              />
            </S.ChevronLink>
          )}

          {pages.map((page) => (
            <S.PageLink
              key={page}
              $isActive={currentRefinement === page}
              aria-label={`Go to page ${page + 1}`}
              data-test-id={`HitsPaginationPageLink-${page + 1}`}
              href={getPaginationUrl(pathname as string, page + 1, locale)}
              onClick={(evt) => {
                evt.preventDefault();
                debouncedHandleClick(page);
              }}
            >
              {page + 1}
            </S.PageLink>
          ))}

          {!isLastPage && (
            <S.ChevronLink
              $isDisabled={isLastPage}
              aria-disabled={isLastPage}
              aria-label={`Go to page ${query.page ?? 1 + 1}`}
              data-test-id='HitsPaginationChevronLink-NextPage'
              href={getPaginationUrl(
                pathname as string,
                currentRefinement + 2,
                locale,
              )}
              onClick={(evt) => {
                if (isLastPage) {
                  return;
                }
                evt.preventDefault();
                debouncedHandleClick(currentRefinement + 1);
              }}
            >
              <Icon
                color={isLastPage ? 'disableGrey' : 'secondary'}
                name='chevronRight'
                variant='bold'
              />
            </S.ChevronLink>
          )}
        </Flex>

        {!isLastPage && (
          <S.PaginationCTA
            data-test-id='HitsPaginationLegacyButton-NextPage'
            disabled={isLastPage}
            label={formatMessage(messages.nextPage)}
            onClick={(evt) => {
              evt.preventDefault();
              debouncedHandleClick(currentRefinement + 1);
            }}
            role='link'
          />
        )}
      </S.Wrapper>
    </>
  );
};

HitsPagination.displayName = 'HitsPagination';
